export interface AttachmentOpenMetadata {
  intent_url: string
  file_url: string
  fallback_url: string
  external_open_url: string
  filename: string | null
  mime_type: string
}

export function attachmentOpenPath(id: string): string {
  return `/api/attachments/${encodeURIComponent(id)}/open`
}

export function attachmentDownloadHref(id: string): string {
  // The server authenticates the current session and signs at the time of
  // this click. Never save a five-minute signed URL as a permanent href.
  return `${attachmentOpenPath(id)}?download=1`
}

export function isAttachmentOpenFresh(fetchedAt: number, now = Date.now()): boolean {
  const age = now - fetchedAt
  return fetchedAt > 0 && age >= 0 && age < 3 * 60 * 1000
}

export async function fetchAttachmentOpen(id: string): Promise<AttachmentOpenMetadata> {
  const response = await fetch(attachmentOpenPath(id), {
    credentials: 'include',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw new Error(`Attachment could not open (${response.status}). Please retry.`)
  return response.json()
}

export interface AgentChatAttachmentLink {
  id: string
  download: boolean
}

// Aliases are supplied by this deployment's build, never embedded in public bundles.
export function parseAgentChatAttachmentLink(
  href: string,
  origin: string,
  configuredAliases: readonly string[] = [],
): AgentChatAttachmentLink | null {
  try {
    const url = new URL(href, origin)
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) return null
    const sameDeployment = url.origin === origin
      || (configuredAliases.includes(origin) && configuredAliases.includes(url.origin))
    if (!sameDeployment) return null
    const match = url.pathname.match(/^\/api\/attachments\/([A-Za-z0-9_-]+)\/(?:open|file)$/)
    if (!match) return null
    return {
      id: match[1]!,
      download: url.searchParams.get('download') === '1'
        || url.searchParams.get('disposition') === 'attachment',
    }
  } catch {
    return null
  }
}
